import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { eventsMenu, eventCategoryContent } from "../constants/eventsData";
import { productionServices } from "../constants/productionData";
import { buildEventSubitemPath } from "../utils/eventSubeventSlug";
import panthiLogo from "../assets/images/Panthi_logo_trans.png";
import "./Navbar.css";

// Group production services into categories for cascading menu
const productionCategories = [
  {
    id: "decoration-setup",
    title: "Decoration & Setup",
    items: productionServices.filter((s) =>
      [
        "decoration",
        "stage-setup",
        "flower-decor",
        "light-decor",
        "furniture-decor",
        "creative-designing",
        "branding-print",
      ].includes(s.id),
    ),
  },
  {
    id: "av-production",
    title: "AV & Production",
    items: productionServices.filter((s) =>
      [
        "sound-audio",
        "led-visual",
        "truss-structure",
        "special-effects",
        "camera-live-streaming",
        "dj-entertainment",
      ].includes(s.id),
    ),
  },
  {
    id: "services",
    title: "Services & Hospitality",
    items: productionServices.filter((s) =>
      [
        "catering",
        "food-hospitality",
        "photobooth",
        "manpower-management",
        "logistics-support",
        "venue-booking",
      ].includes(s.id),
    ),
  },
  {
    id: "management",
    title: "Management & Support",
    items: productionServices.filter((s) =>
      [
        "artist-celebrity-management",
        "security-management",
        "ticketing-registration",
        "event-licensing",
        "virtual-hybrid-event",
        "fire-safety",
        "power-backup",
        "backstage-management",
        "technical-support",
      ].includes(s.id),
    ),
  },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [eventsHoverOpen, setEventsHoverOpen] = useState(false);
  const [eventsClickOpen, setEventsClickOpen] = useState(false);
  const [productionHoverOpen, setProductionHoverOpen] = useState(false);
  const [productionClickOpen, setProductionClickOpen] = useState(false);
  const [eventsHoveredIndex, setEventsHoveredIndex] = useState(-1);
  const [productionHoveredIndex, setProductionHoveredIndex] = useState(-1);
  const location = useLocation();
  const eventsDropdownOpen = eventsHoverOpen || eventsClickOpen;
  const productionDropdownOpen = productionHoverOpen || productionClickOpen;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setEventsHoverOpen(false);
    setEventsClickOpen(false);
    setProductionHoverOpen(false);
    setProductionClickOpen(false);
    setEventsHoveredIndex(-1);
    setProductionHoveredIndex(-1);
  }, [location]);

  const closeNavOnLinkClick = () => {
    setIsMobileMenuOpen(false);
    setEventsHoverOpen(false);
    setEventsClickOpen(false);
    setProductionHoverOpen(false);
    setProductionClickOpen(false);
    setEventsHoveredIndex(-1);
    setProductionHoveredIndex(-1);
  };

  const eventsActiveIndex =
    eventsHoveredIndex >= 0 ? eventsHoveredIndex : eventsDropdownOpen ? 0 : -1;
  const productionActiveIndex =
    productionHoveredIndex >= 0
      ? productionHoveredIndex
      : productionDropdownOpen
        ? 0
        : -1;

  return (
    <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img
            src={panthiLogo}
            alt="Panthi Events"
            className="navbar-logo-img"
          />
          <span className="logo-text">PANTHI EVENTS</span>
        </Link>

        <div
          className={`nav-menu ${isMobileMenuOpen ? "active" : ""}`}
          onClick={(e) => {
            if (e.target.closest("a")) closeNavOnLinkClick();
          }}
        >
          <Link to="/" className="nav-link">
            Home
          </Link>

          {/* Events cascading dropdown */}
          <div
            className="nav-dropdown nav-dropdown-events"
            onMouseEnter={() => setEventsHoverOpen(true)}
            onMouseLeave={() => {
              setEventsHoverOpen(false);
              setEventsHoveredIndex(-1);
            }}
          >
            <div className="nav-dropdown-trigger-row">
              <Link
                to="/events"
                className="nav-link nav-link-events-trigger nav-link-dropdown-parent"
                aria-haspopup="true"
                onClick={closeNavOnLinkClick}
              >
                Events{" "}
                <FaChevronDown
                  className={`nav-chevron nav-chevron-desktop ${eventsDropdownOpen ? "open" : ""}`}
                  aria-hidden
                />
              </Link>
              <button
                type="button"
                className="nav-dropdown-submenu-toggle"
                aria-label="Toggle Events submenu"
                aria-expanded={eventsClickOpen}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEventsClickOpen((prev) => !prev);
                }}
              >
                <FaChevronDown
                  className={`nav-chevron ${eventsClickOpen ? "open" : ""}`}
                  aria-hidden
                />
              </button>
            </div>
            <div
              className={`dropdown-menu cascading-menu ${
                eventsDropdownOpen ? "active" : ""
              }`}
            >
              <div className="cascading-menu-inner">
                <div className="cascading-level cascading-level-1">
                  <Link to="/events" className="cascading-all-link">
                    All Events
                  </Link>
                  <ul className="cascading-list">
                    {eventsMenu.map((category, index) => (
                      <li
                        key={category.id}
                        className={`cascading-item-wrapper ${
                          eventsActiveIndex === index ? "hovered" : ""
                        }`}
                        onMouseEnter={() => setEventsHoveredIndex(index)}
                        onClick={(e) => {
                          if (window.innerWidth <= 768) {
                            e.preventDefault();
                            setEventsHoveredIndex((prev) =>
                              prev === index ? -1 : index,
                            );
                          }
                        }}
                      >
                        <Link
                          to={category.path}
                          className="cascading-item cascading-item-with-arrow"
                        >
                          {category.title}
                          <FaChevronRight className="cascading-arrow" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="cascading-level cascading-level-2">
                  {eventsMenu.map((category, index) => (
                    <div
                      key={category.id}
                      className={`cascading-subpanel ${
                        eventsActiveIndex === index ? "active" : ""
                      }`}
                    >
                      <Link
                        to={category.path}
                        className="cascading-subpanel-title"
                      >
                        {category.title}
                      </Link>
                      <ul className="cascading-sublist">
                        {category.subItems.map((item, i) => (
                          <li key={i}>
                            <Link
                              to={buildEventSubitemPath(
                                item,
                                category.path,
                                eventCategoryContent[category.id]?.subEvents,
                              )}
                              className="cascading-subitem"
                            >
                              {item}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Production cascading dropdown */}
          <div
            className="nav-dropdown nav-dropdown-production"
            onMouseEnter={() => setProductionHoverOpen(true)}
            onMouseLeave={() => {
              setProductionHoverOpen(false);
              setProductionHoveredIndex(-1);
            }}
          >
            <div className="nav-dropdown-trigger-row">
              <Link
                to="/production"
                className="nav-link nav-link-production-trigger nav-link-dropdown-parent"
                aria-haspopup="true"
                onClick={closeNavOnLinkClick}
              >
                Production{" "}
                <FaChevronDown
                  className={`nav-chevron nav-chevron-desktop ${
                    productionDropdownOpen ? "open" : ""
                  }`}
                  aria-hidden
                />
              </Link>
              <button
                type="button"
                className="nav-dropdown-submenu-toggle"
                aria-label="Toggle Production submenu"
                aria-expanded={productionClickOpen}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setProductionClickOpen((prev) => !prev);
                }}
              >
                <FaChevronDown
                  className={`nav-chevron ${productionClickOpen ? "open" : ""}`}
                  aria-hidden
                />
              </button>
            </div>
            <div
              className={`dropdown-menu cascading-menu cascading-menu-production ${
                productionDropdownOpen ? "active" : ""
              }`}
            >
              <div className="cascading-menu-inner">
                <div className="cascading-level cascading-level-1">
                  <Link to="/production" className="cascading-all-link">
                    All Production Services
                  </Link>
                  <ul className="cascading-list">
                    {productionCategories.map((cat, index) => (
                      <li
                        key={cat.id}
                        className={`cascading-item-wrapper ${
                          productionActiveIndex === index ? "hovered" : ""
                        }`}
                        onMouseEnter={() => setProductionHoveredIndex(index)}
                        onClick={() => {
                          if (window.innerWidth <= 768) {
                            setProductionHoveredIndex((prev) =>
                              prev === index ? -1 : index,
                            );
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (
                            window.innerWidth <= 768 &&
                            (e.key === "Enter" || e.key === " ")
                          ) {
                            e.preventDefault();
                            setProductionHoveredIndex((prev) =>
                              prev === index ? -1 : index,
                            );
                          }
                        }}
                      >
                        <span className="cascading-item cascading-item-with-arrow">
                          {cat.title}
                          <FaChevronRight className="cascading-arrow" />
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="cascading-level cascading-level-2">
                  {productionCategories.map((cat, index) => (
                    <div
                      key={cat.id}
                      className={`cascading-subpanel ${
                        productionActiveIndex === index ? "active" : ""
                      }`}
                    >
                      <span className="cascading-subpanel-title">
                        {cat.title}
                      </span>
                      <ul className="cascading-sublist">
                        {cat.items.map((item) => (
                          <li key={item.id}>
                            <Link
                              to={`/production#${item.id}`}
                              className="cascading-subitem"
                            >
                              {item.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <Link to="/gallery" className="nav-link">
            Gallery
          </Link>
          <Link to="/blogs" className="nav-link">
            Blogs
          </Link>
          <Link to="/contact" className="nav-link">
            Contact
          </Link>
        </div>

        <div
          className="nav-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
